import { apiFetch, qs } from '../lib/api';

export type FeedStatus = 'green' | 'amber' | 'red';

export interface FeedEvidence {
  source: string;
  role: 'applied' | 'arrival';
  at: string | null;
  rows: number | null;
  detail: string | null;
  last_status?: string | null;
  last_attempt_at?: string | null;
  error?: string | null;
}

export interface FeedRow {
  key: string;
  label: string;
  vendor: string;
  kind: string;
  cadence: string;
  max_age_hours: number;
  status: FeedStatus;
  reasons: string[];
  last_success_at: string | null;
  age_hours: number | null;
  rows_processed: number | null;
  evidence: FeedEvidence[];
  schedules: Array<{ name: string; role: 'primary' | 'secondary'; state: string; schedule: string | null; source: string }>;
  lambdas: Array<{ name: string; reserved_concurrency: number | null; source: string }>;
  notes: string | null;
}

export interface ScheduleRow {
  name: string;
  state: 'ENABLED' | 'DISABLED' | 'UNKNOWN';
  schedule: string | null;
  description: string;
  target: string | null;
  lambda_fn: string | null;
  script: string | null;
  category: string;
  writes_live: boolean;
  source: 'live' | 'snapshot';
  feeds: string[];
  last_success_at: string | null;
  flagged: boolean;
  flag_reason: string | null;
}

export interface FeedsResponse {
  generated_at: string;
  cached?: boolean;
  feeds: FeedRow[];
  schedules: ScheduleRow[];
  sources: { eventbridge_live_prefixes: string[]; eventbridge_errors: string[]; schedule_snapshot_at: string; lambda: string; s3: string };
  iam_needed: string[];
  counts: { green: number; amber: number; red: number; flagged_schedules: number };
}

export interface HealthRun {
  id: number;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  trigger: string;
  requested_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  heartbeat_at: string | null;
  shopify_exported_at: string | null;
  products_checked: number | null;
  variants_checked: number | null;
  issue_count: number | null;
  stats: Record<string, any> | null;
  error: string | null;
  total?: number;
  total_in_stock?: number | null;
}

export interface HealthClass {
  id: string;
  group: string;
  level: 'fail' | 'warn' | 'info';
  title: string;
  description: string;
  ported_from: string;
  count: number;
  in_stock: number;
  fail: number;
  top_vendors: Array<[string, number]>;
  prev_count: number | null;
  prev_in_stock: number | null;
  delta: number | null;
  delta_in_stock: number | null;
}

export interface HealthResponse {
  ready: boolean;
  note?: string;
  latest: HealthRun | null;
  previous: HealthRun | null;
  active: HealthRun[];
  last_failed: HealthRun | null;
  schedule?: { interval_hours: number; runs_in: string };
  classes: HealthClass[];
}

export interface IssueRow {
  id: string;
  class: string;
  class_title: string;
  level: string;
  sku: string | null;
  vendor: string | null;
  feed: string | null;
  in_stock: boolean;
  shopify_value: string | null;
  vendor_value: string | null;
  shopify_product_id: string | null;
  shopify_variant_id: string | null;
  handle: string | null;
  title: string | null;
  details: Record<string, unknown>;
  severity: number;
  admin_url: string | null;
  storefront_url: string | null;
}

export interface IssuesParams {
  run_id?: number;
  class?: string;
  vendor?: string;
  in_stock?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface Paged<T> {
  data: T[];
  meta: { total: number; limit: number; offset: number; run_id: number | null };
}

export interface ChangeRow {
  id: string;
  run_id: number;
  observed_at: string;
  window_start: string | null;
  field: string;
  before_value: string | null;
  after_value: string | null;
  sku: string | null;
  vendor: string | null;
  handle: string | null;
  shopify_product_id: string | null;
  shopify_variant_id: string | null;
  admin_url: string | null;
  storefront_url: string | null;
}

export interface ChangeSummaryRow {
  run_id: number;
  observed_at: string | null;
  window_start: string | null;
  trigger: string;
  fields: Record<string, number>;
  baseline: boolean | null;
}

export interface SyncRunRow {
  source_table: string;
  id: string;
  feed: string;
  status: string;
  dry_run: boolean;
  rows: number | null;
  inserted: number | null;
  updated: number | null;
  invalid: number | null;
  created_at: string;
  applied_at: string | null;
  error: string | null;
}

export const getSyncFeeds = (refresh = false) =>
  apiFetch<FeedsResponse>(`/admin/sync-center/feeds${refresh ? '?refresh=1' : ''}`);
export const getHealth = () => apiFetch<HealthResponse>('/admin/sync-center/health');
export interface ConnectionCheck {
  key: string; label: string; vendor: string; verdict: 'pass' | 'warn' | 'fail' | 'n/a';
  checked: number; issues: number; in_stock_issues: number; fail: number; top_classes: Array<[string, number]>;
}
export interface VerificationResponse { ready: boolean; run: { id: number; finished_at: string | null } | null; connections: ConnectionCheck[] }
export const getVerification = () => apiFetch<VerificationResponse>('/admin/sync-center/verification');
export const getHealthRuns = (limit = 20) => apiFetch<{ data: HealthRun[] }>(`/admin/sync-center/health/runs${qs({ limit })}`);
export const getIssues = (p: IssuesParams) =>
  apiFetch<Paged<IssueRow>>(`/admin/sync-center/health/issues${qs(p as Record<string, unknown>)}`);
export const getIssueVendors = (p: { run_id?: number; class?: string }) =>
  apiFetch<{ data: Array<{ vendor: string; count: number; in_stock: number }>; run_id?: number }>(
    `/admin/sync-center/health/vendors${qs(p as Record<string, unknown>)}`,
  );
export const runHealthNow = () =>
  apiFetch<{ run_id: number; status: string; note: string }>('/admin/sync-center/health/run', { method: 'POST' });
export const getChanges = (p: { run_id?: number; field?: string; vendor?: string; q?: string; limit?: number; offset?: number }) =>
  apiFetch<Paged<ChangeRow>>(`/admin/sync-center/changes${qs(p as Record<string, unknown>)}`);
export const getChangeSummary = () => apiFetch<{ data: ChangeSummaryRow[] }>('/admin/sync-center/changes/summary');
export const getSyncRuns = (limit = 50) => apiFetch<{ data: SyncRunRow[] }>(`/admin/sync-center/sync-runs${qs({ limit })}`);
